import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { userService, playerService } from "../../services/api";
import { ROLES } from "../../config/roles";

import CoachHeroBanner from "../../components/common/CoachHeroBanner";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import CoachPersonalInfo from "../../components/profile/CoachPersonalInfo";
import CoachStatistics from "../../components/profile/CoachStatistics";
import ReportDataIssueButton from "../../components/common/ReportDataIssueButton";
import AddToRadarButton from "../../components/agent-radar/profile/AddToRadarButton";
import ClubAddToRadarButton from "../../components/club-radar/profile/ClubAddToRadarButton";
import { canUseClubRadar } from "../../utils/clubRoles";
import CoachCoachingStyle from "../../components/profile/CoachCoachingStyle";
import CoachingPersonalityCard from "../../components/profile/coaching-personality/CoachingPersonalityCard";
import { coachingPersonalityService } from "../../services/coachingPersonalityApi";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation, getStaticTemplateMatch } from "../../translations/registerStaticTranslations";

const CoachProfile = () => {
  const { coachId } = useParams();
  const { currentRole } = useSelector((state) => state.auth);
  const permissions = useSelector((state) => state.auth.permissions);
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — look the sentence up the same way the scanner does (exact
  // match first, template match second, e.g. for the "{count}" share count).
  const currentLanguage = useUiLanguage();
  const tr = (s) =>
    getStaticTranslation(i18n, s, currentLanguage) ||
    getStaticTemplateMatch(i18n, s, currentLanguage) ||
    s;
  // Personal Info opens first (Marin 2026-08-21: „Personal Info als 1.,
  // dann Statistics, Coaching Style — sieht viel sauberer aus").
  const [activeTab, setActiveTab] = useState("personal-info");
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  // Personality-Rest (Marin 2026-08-24): the coach on their OWN public profile
  // sees their actual sharing setting. null = loading, 'none' = no completed
  // assessment, true/false = is_released. Fetched only for the own profile.
  const [ownSharing, setOwnSharing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCoachData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch coach data from API
        const response = await playerService.getPublicCoachProfile(coachId);

        if (response.success) {
          const coachData = response.data;

          // Transform API response to match component expectations
          const transformedCoach = {
            // Basic info
            id: coachData.id,
            ts_coach_id: coachData.ts_coach_id,
            public_coach_id: coachData.public_coach_id,
            name: coachData.name,
            display_name: coachData.name,
            age: coachData.age,
            position: coachData.position,
            specialization: coachData.specialization,
            email: coachData.email,
            mobile: coachData.mobile,

            // Profile picture
            logo: coachData.profile_picture_url,
            image_path: coachData.profile_picture_url,
            profile_picture_url: coachData.profile_picture_url,

            // Club/Team info. Platform club id for the click-through
            // (Marin 2026-08-21: club in the hero must be clickable):
            // prefer an explicit club_details.id when the BE ships one;
            // until then derive it from the resume — the newest station of
            // the SAME club carries the platform team id (same sync).
            team: coachData.club_details
              ? {
                  name: coachData.club_details.name,
                  logo: coachData.club_details.logo,
                  image_path: coachData.club_details.logo,
                  platform_club_id:
                    coachData.club_details.platform_club_id ??
                    coachData.club_details.id ??
                    (coachData.resume || []).find(
                      (st) =>
                        st?.team?.id &&
                        String(st.team.name || "").trim().toLowerCase() ===
                          String(coachData.club_details.name || "")
                            .trim()
                            .toLowerCase()
                    )?.team?.id ??
                    null,
                }
              : null,
            team_name: coachData.club_details?.name,
            team_logo: coachData.club_details?.logo,

            // Representation from the active coach mandate (BE PR #447,
            // Marins Fund: "was wenn der Trainer bei einer Agentur ist?").
            // Same minimal blocks as the player profile; null renders nothing.
            agent: coachData.agent || null,
            agency: coachData.agency || null,

            // Nationality - handle as object for CoachHeroBanner
            nationality: {
              name: coachData.nationality || "",
              code: coachData.nationality || "",
              image_url: coachData.nationality_logo || null,
            },
            nationality_image: coachData.nationality_logo,
            nationality_logo: coachData.nationality_logo,

            // Formation
            preferred_formation:
              coachData.top_formations?.[0] || coachData.preferred_formation,
            top_formations: coachData.top_formations || [],

            // Trophies - transform to match CoachPersonalInfo expectations
            trophies: (coachData.trophies || []).map((trophy) => ({
              id: trophy.id,
              league_name: trophy.title,
              club_name: trophy.club,
              years: trophy.year?.toString(),
              tournament_image: trophy.image_url,
            })),

            // Experience - transform to match CoachPersonalInfo expectations
            experience: (coachData.experiences || []).map((exp) => {
              const isPresent = exp.end_year === null || exp.end_month === null;
              return {
                id: exp.id,
                club_name: exp.club_name,
                position_name: exp.position,
                place: exp.country,
                start_date:
                  exp.start_year && exp.start_month
                    ? `${String(exp.start_month).padStart(2, "0")}/${
                        exp.start_year
                      }`
                    : "",
                end_date: isPresent
                  ? "Present"
                  : exp.end_year && exp.end_month
                  ? `${String(exp.end_month).padStart(2, "0")}/${exp.end_year}`
                  : "Present",
                club_image: null, // API doesn't provide this, can be enhanced later
                type: "Full-time", // Default type
              };
            }),

            // Licenses - transform to array of strings
            licenses: (coachData.licenses || []).map(
              (license) => license.license_type || license
            ),

            // Languages - transform to array of strings
            languages: (coachData.languages || []).map(
              (lang) => lang.language || lang
            ),

            // Playing Style
            playingStyle: coachData.playing_styles || [],

            // Defensive Tactics
            defensiveTactics: coachData.defensive_tactics || [],

            // Offensive Tactics
            offensiveTactics: coachData.offensive_tactics || [],

            // Additional Notes
            additionalNotes: coachData.additional_notes || "",

            // Statistics - keep as array from API
            statistics: coachData.statistics || [],

            // Contract line + Career tab (Coach-Profile-Data, BE PR #442):
            // epochs verbatim; `resume` are the TheSports coaching stations.
            joined: coachData.joined,
            contract_until: coachData.contract_until,
            birthday: coachData.birthday,
            careerStations: coachData.resume || [],

            // Resume
            resume_url: coachData.resume_url,

            // KYC
            kyc_verified: coachData.kyc_verified || false,
          };

          setCoach(transformedCoach);
        } else {
          throw new Error(response.error || "Failed to fetch coach data");
        }
      } catch (error) {
        console.error("Error fetching coach data:", error);
        setError(error.message || "Failed to load coach data.");
      }

      setLoading(false);
    };

    if (coachId) {
      fetchCoachData();
    }
  }, [coachId]);

  // URL-Umstellung (Marin 2026-08-24): the visible URL carries the platform
  // number. Old TheSports-uuid links keep resolving (the server accepts both
  // id families, PR #454) and are canonicalized here — after the replace the
  // ids match, so this never loops.
  useEffect(() => {
    const canonicalId = coach?.public_coach_id;
    if (canonicalId && String(canonicalId) !== String(coachId)) {
      navigate(`/search/coach/${canonicalId}`, { replace: true });
    }
  }, [coach, coachId, navigate]);

  // Personality-Rest (Marin 2026-08-24): detect "the coach views their own
  // public profile" via the coach's OWN profile endpoint (carries ts_coach_id;
  // the generic /users/profile/ does not) and load their sharing state from
  // the personality endpoint — no new visibility surface, both exist.
  const [ownTsCoachId, setOwnTsCoachId] = useState(null);
  useEffect(() => {
    if (currentRole !== ROLES.Coach) return undefined;
    let alive = true;
    userService.getCoachProfile().then((res) => {
      if (alive && res.success) setOwnTsCoachId(res.data?.ts_coach_id || null);
    }).catch(() => {});
    return () => { alive = false; };
  }, [currentRole]);

  const isOwnCoachProfile =
    currentRole === ROLES.Coach &&
    !!ownTsCoachId &&
    !!coach?.ts_coach_id &&
    String(ownTsCoachId) === String(coach.ts_coach_id);

  useEffect(() => {
    if (!isOwnCoachProfile) return undefined;
    const controller = new AbortController();
    coachingPersonalityService.getState(controller.signal).then((res) => {
      if (!res.success) return;
      const result = res.data?.result;
      setOwnSharing(result ? !!result.is_released : "none");
    }).catch(() => {});
    return () => controller.abort();
  }, [isOwnCoachProfile]);

  // Fetch current user profile to decide Share button visibility
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await userService.getProfile();
        if (res.success) setCurrentUserProfile(res.data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    fetchProfile();
  }, []);

  const tabs = [
    { id: "personal-info", label: "Personal Info" },
    { id: "statistics", label: "Statistics" },
    { id: "coaching-style", label: "Coaching Style" },
    { id: "personality", label: "Personality" },
  ];

  const handleShare = async (selectedUsers, shareUrl) => {
    try {
      console.log("Sharing with users:", selectedUsers);
      console.log("Share URL:", shareUrl);
      alert(tr(`Shared coach profile with ${selectedUsers.length} user(s)!`));
    } catch (error) {
      console.error("Error sharing:", error);
      alert(tr("Failed to share. Please try again."));
    }
  };

  const shareUrl = `${window.location.origin}/search/coach/${coachId}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Coach not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      {/* Header row (Supports 360px-Audit 2026-08-21): mobile-taugliches
          Padding + WRAP — die Button-Gruppe (Radar/Report/Share) rutscht bei
          schmalen Breiten als eigene Zeile unter den Namen, statt vertikal
          neben dem gequetschten Namen zu stapeln. */}
      <div className="w-full px-4 md:px-12 py-4 flex flex-wrap items-center justify-between gap-2 border-b border-[#E5E6E8]">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.8327 7.00033H1.16602M1.16602 7.00033L6.99935 12.8337M1.16602 7.00033L6.99935 1.16699"
              stroke="#1E1E1E"
              strokeWidth="1.875"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xl font-bold text-[#222A35]">
            {coach?.name || coach?.sportmonks_data?.display_name || "Coach"}
          </span>
        </div>
        {(() => {
          const worksForClub = currentUserProfile?.user_details?.works_for_club;
          const hideForStaffNoClub =
            currentRole === ROLES.Staff && worksForClub === false;
          if (hideForStaffNoClub) return null;
          return (
            <div className="flex flex-wrap items-center gap-2">
            {/* Add to Radar (Coach-Radar Phase 1+2): the full player-header
                role split (HeaderActions R10) — agents get the agent-radar
                button, club users WITH radar access the club-radar button,
                everyone else nothing. Keyed on the TSCoach id the whole
                coach surface keys on. */}
            {currentRole === ROLES.Agent || currentRole === ROLES.AgencyAdmin ? (
              <AddToRadarButton tsCoachId={coachId} />
            ) : canUseClubRadar(currentRole, permissions) ? (
              <ClubAddToRadarButton tsCoachId={coachId} playerName={coach?.name} />
            ) : null}
            {/* Report data issue (Coach-Profile-Data): coachId IS the TSCoach
                id — exactly what the coach lane of the data-feedback endpoint
                expects (entity_type "coach" + ts_coach_id). */}
            <ReportDataIssueButton
              entityType="coach"
              tsCoachId={coachId}
              entityName={coach?.name}
            />
            <button
              type="button"
              className="border border-[#E6E6E6] rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
              onClick={() => setShowShareModal(true)}
              aria-label="Share coach profile"
            >
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.72667 9.00634L9.28 11.6597M9.27333 4.33967L4.72667 6.99301M13 3.33301C13 4.43758 12.1046 5.33301 11 5.33301C9.89543 5.33301 9 4.43758 9 3.33301C9 2.22844 9.89543 1.33301 11 1.33301C12.1046 5.33301 13 2.22844 13 3.33301ZM5 7.99967C5 9.10424 4.10457 9.99967 3 9.99967C1.89543 9.99967 1 9.10424 1 7.99967C1 6.8951 1.89543 5.99967 3 5.99967C4.10457 5.99967 5 6.8951 5 7.99967ZM13 12.6663C13 13.7709 12.1046 14.6663 11 14.6663C9.89543 14.6663 9 13.7709 9 12.6663C9 11.5618 9.89543 10.6663 11 10.6663C12.1046 10.6663 13 11.5618 13 12.6663Z"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-[#1E1E1E]">Share</span>
            </button>
            </div>
          );
        })()}
      </div>

      {/* Hero Section Banner */}
      <div className="w-full px-6 md:px-10 pt-6">
        <CoachHeroBanner coach={coach} />
      </div>

      {/* Tabs */}
      <div className="border-b bg-white sticky top-0 z-20">
        <div className="w-full mx-auto px-4 md:px-12">
          <div className="flex justify-between items-center">
            {/* Mobile (UI rule 1): with the 4th tab (Career) the row exceeds
                375px — it SCROLLS sideways (whitespace-nowrap buttons), the
                page itself never overflows. */}
            <div className="flex gap-4 md:gap-8 overflow-x-auto whitespace-nowrap">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`my-4 py-2 px-2 rounded-lg font-medium relative ${
                    activeTab === tab.id
                      ? "bg-[#F3F4F6]"
                      : "text-[#0B0B0B] hover:text-gray-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content — mobile padding analog Header (px-12 erst ab md:) */}
      <div className="w-full mx-auto px-4 md:px-12 py-8 bg-white">
        {/* Personal Info Tab Content */}
        {activeTab === "personal-info" && (
          <CoachPersonalInfo coach={coach} />
        )}

        {/* Personality Tab (Marin 2026-08-21): the tab is ALWAYS visible so
            the feature is discoverable — the card itself keeps its server
            rule untouched (it renders only for a club this coach pitched,
            with the switch on; everyone else gets the same 404 and sees only
            the lock note below). `coachId` IS the TheSports id the card's
            route takes (users/urls.py:383); the account id is deliberately
            absent from this page's payload and is NOT substituted for. */}
        {activeTab === "personality" && (
          <div className="space-y-4">
            <div className="w-full flex items-start gap-3 p-4 bg-[#F8F9FB] border border-gray-100 rounded-lg">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className="mt-0.5 flex-shrink-0"
              >
                <path
                  d="M7 10V7a5 5 0 0 1 10 0v3m-11 0h12a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z"
                  stroke="#475569"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isOwnCoachProfile ? (
                /* The coach on their OWN public profile sees their actual
                   sharing setting instead of the generic note (Marins
                   Personality-Rest, 2026-08-24). State comes from the coach's
                   own personality endpoint — no new visibility surface. */
                <p className="text-[#475569] text-sm">
                  Get an overview of your leadership style — results are visible
                  to clubs you have pitched.{" "}
                  {ownSharing === null ? (
                    <span className="text-[#94A3B8]">Checking your sharing setting…</span>
                  ) : ownSharing === "none" ? (
                    <span className="font-semibold">
                      You haven&rsquo;t completed the assessment yet — start it from
                      your profile&rsquo;s Personality section.
                    </span>
                  ) : ownSharing ? (
                    <span className="font-semibold text-[#16A34A]">
                      Your sharing is ON — clubs you pitched can view your results.
                    </span>
                  ) : (
                    <span className="font-semibold text-[#B45309]">
                      Your sharing is OFF — no club can view your results. Turn it
                      on in your profile&rsquo;s Personality section.
                    </span>
                  )}
                </p>
              ) : (
                /* Marins Wunsch (2026-08-21): erklären, WAS die Funktion bietet,
                   auch für alle, die sie nicht öffnen können. */
                <p className="text-[#475569] text-sm">
                  Get an overview of this coach&rsquo;s leadership style — a character
                  assessment the coach completes on TransferPitch. Results are
                  visible to clubs this coach has pitched, with sharing enabled by
                  the coach.
                </p>
              )}
            </div>
            <CoachingPersonalityCard
              tsCoachId={coachId}
              className="max-w-[420px]"
            />
          </div>
        )}

        {/* No separate Career tab (Marins final call 2026-08-21): the merged
            CV lives as the Experience section INSIDE Personal Info. */}

        {/* Statistics Tab Content */}
        {activeTab === "statistics" && (
          <CoachStatistics
            statistics={coach.statistics || []}
            topFormations={coach.top_formations || []}
          />
        )}

        {/* Coaching Style Tab Content */}
        {activeTab === "coaching-style" && <CoachCoachingStyle coach={coach} />}
      </div>

      <ShareActionSheet
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        title={`Share ${coach?.name || coach?.display_name || "Coach"} Profile`}
        shareUrl={shareUrl}
        shareType="coach_public_profile"
        shareData={{
          coach: {
            id: coach?.id,
            name:
              coach?.name ||
              coach?.sportmonks_data?.display_name ||
              "Coach Name",
          },
        }}
      />
    </div>
  );
};

export default CoachProfile;
